import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import {
  convertDiagnosesFlatAnswersToFormData,
  convertOtherDiagnosesToFlatAnswers,
  convertPrimaryDiagnosisToFlatAnswers
} from '../../../../../helpers.uncontrolled'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Background subsection adapter
 * Handles diagnoses, equipment, allergies, and other background medical info (12 fields)
 */
export class BackgroundAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract diagnosis data using existing helper
    const { primaryDiagnosis, otherDiagnoses } = convertDiagnosesFlatAnswersToFormData(
      getAnswerValuesFromItems(
        flatAnswersArray,
        RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS
      ),
      getAnswerValuesFromItems(
        flatAnswersArray,
        RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS
      )
    )

    // Direct field extractions
    const backgroundEquipment = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT
    )

    const backgroundEquipmentOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER
    )?.at(0)

    const backgroundDmeProvider = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER
    )?.at(0)

    const backgroundAllergies = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES
    )

    const backgroundAllergiesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES
    )?.at(0)

    const backgroundWaivers = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS
    )

    const backgroundAdditionalServices = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES
    )

    const backgroundPrognosis = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS
    )?.at(0)

    const backgroundDnr = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR
    )?.at(0)

    const backgroundAdvanceDirectives = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES
    )?.at(0)

    const backgroundAdvanceDirectivesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES
    )?.at(0)

    return {
      [RocFormField.BACKGROUND_PRIMARY_DIAGNOSIS]: primaryDiagnosis,
      [RocFormField.BACKGROUND_OTHER_DIAGNOSIS]: otherDiagnoses,
      [RocFormField.BACKGROUND_EQUIPMENT]: backgroundEquipment,
      [RocFormField.BACKGROUND_EQUIPMENT_OTHER]: backgroundEquipmentOther,
      [RocFormField.BACKGROUND_DME_PROVIDER]: backgroundDmeProvider,
      [RocFormField.BACKGROUND_ALLERGIES]: backgroundAllergies,
      [RocFormField.BACKGROUND_ALLERGIES_NOTES]: backgroundAllergiesNotes,
      [RocFormField.BACKGROUND_WAIVERS]: backgroundWaivers,
      [RocFormField.BACKGROUND_ADDITIONAL_SERVICES]: backgroundAdditionalServices,
      [RocFormField.BACKGROUND_PROGNOSIS]: backgroundPrognosis,
      [RocFormField.BACKGROUND_DNR]: backgroundDnr,
      [RocFormField.BACKGROUND_ADVANCE_DIRECTIVES]: backgroundAdvanceDirectives,
      [RocFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES]: backgroundAdvanceDirectivesNotes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.BACKGROUND_PRIMARY_DIAGNOSIS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS,
        value: convertPrimaryDiagnosisToFlatAnswers(
          this.getFormDataField(RocFormField.BACKGROUND_PRIMARY_DIAGNOSIS)
        )
      })
    }
    if (this.formDataFieldExists(RocFormField.BACKGROUND_OTHER_DIAGNOSIS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS,
        value: convertOtherDiagnosesToFlatAnswers(
          this.getFormDataField(RocFormField.BACKGROUND_OTHER_DIAGNOSIS) ?? []
        )
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_EQUIPMENT)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT,
        value: this.getFormDataField(RocFormField.BACKGROUND_EQUIPMENT)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_EQUIPMENT_OTHER)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER,
        value: this.getFormDataField(RocFormField.BACKGROUND_EQUIPMENT_OTHER)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_DME_PROVIDER)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER,
        value: this.getFormDataField(RocFormField.BACKGROUND_DME_PROVIDER)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_ALLERGIES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES,
        value: this.getFormDataField(RocFormField.BACKGROUND_ALLERGIES)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_ALLERGIES_NOTES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES,
        value: this.getFormDataField(RocFormField.BACKGROUND_ALLERGIES_NOTES)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_WAIVERS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS,
        value: this.getFormDataField(RocFormField.BACKGROUND_WAIVERS)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_ADDITIONAL_SERVICES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES,
        value: this.getFormDataField(RocFormField.BACKGROUND_ADDITIONAL_SERVICES)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_PROGNOSIS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS,
        value: this.getFormDataField(RocFormField.BACKGROUND_PROGNOSIS)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_DNR)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR,
        value: this.getFormDataField(RocFormField.BACKGROUND_DNR)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_ADVANCE_DIRECTIVES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES,
        value: this.getFormDataField(RocFormField.BACKGROUND_ADVANCE_DIRECTIVES)
      })
    }

    if (this.formDataFieldExists(RocFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES,
        value: this.getFormDataField(RocFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES)
      })
    }

    return mutations
  }
}
